import { useMemo, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { ACRouterLink } from 'academy-common-lib/components/ACRouterLink';
import NoResultsError from 'academy-common-lib/components/Errors/NoResultsError';
import { HasGate } from 'academy-common-lib/features/accounts/components/HasGate';
import { CardsSection } from 'academy-common-lib/features/cardsSection/components/CardsSection';
import { useResolvedOnboardingSeriesEvents } from 'academy-common-lib/features/liveEvents/onboardingSeriesLanguage';
import { LiveEventFooter } from 'academy-common-lib/features/liveEvents/components/LiveEventFooter';
import { toLiveEventCardModel } from 'academy-common-lib/features/liveEvents/toLiveEventCardModel';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { FiltersBar } from 'academy-common-lib/features/filters/components/FiltersCard';
import { LanguageFilter } from 'academy-common-lib/features/filters/components/LanguageFilter';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import ApiError from '../../../components/Errors/ApiError';
import OnboardingSectionLayout from './OnboardingSectionLayout';
import { PageSectionHeading } from '../../../components/SectionHeading';
import { ACADEMY_ONBOARDING_SECTION_IDS } from '../constants/sectionAnchors';
import { AO_SUPPORTED_LANGUAGES } from '../constants/supportedLanguages';
import { ACADEMY_ONBOARDING_URL_FRAGMENT, MYLEARNING_URL_FRAGMENT } from '../../../constants/Navigation.legacy';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VIEW_REGISTERED_EVENTS_TO = `${MYLEARNING_URL_FRAGMENT}?contentType=LIVE_EVENT`;
const PAGE_SIZE = 4;
const LiveEventSection = () => {
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const [pageStart, setPageStart] = useState(0);
  const [maxCards, setMaxCards] = useState(PAGE_SIZE);
  const pageSize = isMobile ? PAGE_SIZE : maxCards;
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const selectedLanguage = selectedLanguages[0];

  // Leaving this undefined lets the hook resolve the UI language and fall back
  // to the English series.
  const language = selectedLanguage ? convertToSupportedLanguage(selectedLanguage) : undefined;
  const handleLanguageChange = languages => {
    setSelectedLanguages(languages);
    setPageStart(0);
  };
  const handleResetFilters = () => {
    setSelectedLanguages([]);
    setPageStart(0);
  };
  const {
    orderedIds,
    sourceEvents,
    loading: isLiveEventsLoading,
    error: seriesEventsError
  } = useResolvedOnboardingSeriesEvents({
    userSelectedLanguage: language
  });
  const eventsByCardId = useMemo(() => {
    const result = new Map();
    sourceEvents.forEach(event => result.set(event.id, event));
    return result;
  }, [sourceEvents]);
  const cardModels = useMemo(() => orderedIds.map(id => eventsByCardId.get(id)).filter(event => !!event).map(toLiveEventCardModel), [eventsByCardId, orderedIds]);
  const visibleCardModels = cardModels.slice(pageStart, pageStart + pageSize);
  const handleClickNext = () => setPageStart(start => start + pageSize);
  const handleClickPrevious = () => setPageStart(start => Math.max(0, start - pageSize));
  const showNext = pageStart + pageSize < cardModels.length;
  const showPrevious = pageStart > 0;
  const hasRegisteredEvents = sourceEvents.some(e => e.nextRegisteredSession || e.attended);
  const descriptionContent = /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "200",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.academyOnboarding.liveEvent.description"
      })
    }), hasRegisteredEvents && /*#__PURE__*/_jsx(HasGate, {
      gate: "AcademyAchievements::MyLearningSearchLiveEventRollout",
      children: /*#__PURE__*/_jsx(ACRouterLink, {
        to: VIEW_REGISTERED_EVENTS_TO,
        underline: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.academyOnboarding.liveEvent.showRegistered"
        })
      })
    })]
  });
  const sectionHeader = /*#__PURE__*/_jsxs(Flex, {
    alignItems: isMobile || isTablet ? 'stretch' : 'center',
    flexDirection: isMobile || isTablet ? 'column' : 'row',
    gap: "400",
    justifyContent: "space-between",
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(Flex, {
      flex: "1",
      flexDirection: "column",
      children: /*#__PURE__*/_jsx(PageSectionHeading, {
        marginBlockEnd: "0",
        subtitle: descriptionContent,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.academyOnboarding.liveEvent.title"
        })
      })
    }), /*#__PURE__*/_jsxs(FiltersBar, {
      shouldRenderClearFilters: false,
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.academyOnboarding.resources.filterLabel"
        })
      }), /*#__PURE__*/_jsx(LanguageFilter, {
        appearsIn: "academy-onboarding-live-event",
        languages: AO_SUPPORTED_LANGUAGES,
        onChange: handleLanguageChange,
        value: selectedLanguages
      })]
    })]
  });
  const isEmpty = !isLiveEventsLoading && !seriesEventsError && cardModels.length === 0;
  if (!isLiveEventsLoading && seriesEventsError) {
    return /*#__PURE__*/_jsxs(OnboardingSectionLayout, {
      id: ACADEMY_ONBOARDING_SECTION_IDS.liveEvents,
      paddingBlockEnd: "600",
      paddingBlockStart: "600",
      children: [sectionHeader, /*#__PURE__*/_jsx(NavMarker, {
        name: "ACADEMY_ONBOARDING_LIVE_EVENTS_API_ERROR",
        children: /*#__PURE__*/_jsx(ApiError, {
          error: seriesEventsError,
          resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
        })
      })]
    });
  }
  if (isEmpty) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "ACADEMY_ONBOARDING_LIVE_EVENTS_LOADED",
      children: /*#__PURE__*/_jsxs(OnboardingSectionLayout, {
        id: ACADEMY_ONBOARDING_SECTION_IDS.liveEvents,
        paddingBlockEnd: "600",
        paddingBlockStart: "600",
        children: [sectionHeader, /*#__PURE__*/_jsx(NoResultsError, {
          onReset: handleResetFilters,
          resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
        })]
      })
    });
  }
  const renderFooter = cardModel => {
    const liveEventModel = cardModel;
    const event = eventsByCardId.get(liveEventModel.id);
    if (!event) return null;
    return /*#__PURE__*/_jsx(LiveEventFooter, {
      attended: event.attended,
      eventType: event.eventType,
      nextRegisteredSession: event.nextRegisteredSession,
      nextUpcomingEventSession: event.nextUpcomingEventSession
    });
  };
  return /*#__PURE__*/_jsxs(OnboardingSectionLayout, {
    id: ACADEMY_ONBOARDING_SECTION_IDS.liveEvents,
    paddingBlockEnd: "600",
    paddingBlockStart: "600",
    children: [sectionHeader, /*#__PURE__*/_jsx(CardsSection, {
      appearsIn: "academy-onboarding-live-event",
      content: visibleCardModels,
      direction: isMobile ? 'column' : 'row',
      isLoading: isLiveEventsLoading,
      maxSkeletonListLength: 3,
      navMarker: "ACADEMY_ONBOARDING_LIVE_EVENTS_LOADED",
      onClickNext: handleClickNext,
      onClickPrevious: handleClickPrevious,
      overviewType: "academy-onboarding-live-event",
      renderFooter: renderFooter,
      setMaxCards: isMobile ? undefined : setMaxCards,
      showNext: showNext,
      showOverview: true,
      showPrevious: showPrevious,
      size: isMobile ? 'small' : 'large'
    })]
  });
};
export default LiveEventSection;